import {
  Box,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useMemo } from 'react';

import Loader from '../../Loader/Loader';

export interface ITableColumn<T> {
  label: string | React.ReactNode;
  headerCellStyle?: SxProps<Theme>;
  bodyCellStyle?: SxProps<Theme>;
  onHeaderCellClick?: (item: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => void;
  onBodyCellClick?: (item: T) => void;
  onBodyCellDoubleClick?: (item: T) => void;
  onCellContextMenu?: (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>, item: T) => void;
  render: (item: T) => React.ReactNode;
}

interface ITableProps<T> {
  data?: T[] | null;
  columns: ITableColumn<T>[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  isLoading: boolean;
  perPageOptions?: (number | { value: number; label: string })[];
  containerStyle?: SxProps<Theme>;
  maxHeight?: string | number;
  emptyPlaceholderTitle?: string;
  emptyPlaceholderSubtitle?: string;
  menuAnchorEl?: HTMLElement | null;
  handleMenuClose?: () => void;
  activeRow?: T | null;
}

export const TableMUI = <T, B>({
  data,
  columns,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  isLoading,
  perPageOptions,
  containerStyle,
  maxHeight = 440,
  emptyPlaceholderTitle = 'No items to show',
  emptyPlaceholderSubtitle = 'Nothing to display at the moment',
  menuAnchorEl,
  handleMenuClose,
  activeRow,
}: ITableProps<T>) => {
  const theme = useTheme();

  const placeholder = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 400,
        }}
      >
        <Typography fontWeight={theme.typography.fontWeightMedium}>{emptyPlaceholderTitle}</Typography>
        <Typography sx={{ fontSize: 14 }}>{emptyPlaceholderSubtitle}</Typography>
      </Box>
    ),
    [emptyPlaceholderSubtitle, emptyPlaceholderTitle, theme.typography.fontWeightMedium],
  );

  const onContextMenu = React.useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, item: T, column: ITableColumn<T>) => {
      e.preventDefault();
      if (menuAnchorEl) {
        handleMenuClose?.();
      }
      column.onCellContextMenu?.(e, item);
    },
    [menuAnchorEl, handleMenuClose],
  );

  return (
    <Box sx={containerStyle ?? {}}>
      {isLoading ? (
        <Box sx={{ position: 'relative', height: 440 }}>
          <Loader lowPriority isLoading={isLoading} />
        </Box>
      ) : (
        <>
          {!data || data.length === 0 ? (
            placeholder
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight, overflow: 'auto', width: '100%' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((column, index) => {
                      return (
                        <TableCell
                          onClick={(e) => column?.onHeaderCellClick?.(e)}
                          sx={column.headerCellStyle}
                          key={index}
                          style={{ fontWeight: theme.typography.fontWeightMedium, zIndex: 10 }}
                        >
                          {column.label}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, rowIndex) => {
                    const isActiveRow = activeRow === row;
                    return (
                      <TableRow
                        key={rowIndex}
                        hover
                        data-has-menu={isActiveRow}
                        sx={{
                          backgroundColor: (th) => (isActiveRow ? th.sass.muiDefaultTableHoverColor : 'inherit'),
                          '&:hover': {
                            backgroundColor: (th) => th.sass.muiDefaultTableHoverColor,
                          },
                        }}
                      >
                        {columns.map((column, colIndex) => (
                          <TableCell
                            onContextMenu={(e) => onContextMenu(e, row, column)}
                            onDoubleClick={() => column?.onBodyCellDoubleClick?.(row)}
                            onClick={() => column?.onBodyCellClick?.(row)}
                            sx={{
                              ...column.bodyCellStyle,
                              '& .MuiTypography-root, & span': {
                                fontSize: '14px',
                              },
                            }}
                            key={colIndex}
                          >
                            {column.render(row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    zIndex: 10,
                  }}
                >
                  <TableRow>
                    <TablePagination
                      sx={{ borderTop: `1px solid ${theme.sass.tableBorderColor}` }}
                      rowsPerPageOptions={perPageOptions}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      page={page - 1}
                      onPageChange={(_, newPage) => onPageChange(newPage + 1)}
                      onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};
